/**
 * Utility functions for handling German license plates
 */

// Regex patterns for plate validation
export const PLATE_REGEX = /^(ohne|OHNE|Ohne|[A-ZÄÖÜ]{1,3}[\s-]?[A-ZÄÖÜ]{0,2}[\s-]?\d{1,4}[EH]?)$/i

/**
 * Parse HubSpot plate format (CITY-LETTERNUMBER) into display format (CITY LETTERS NUMBERS)
 * @param plate - The plate string in HubSpot format
 * @returns The plate in display format with spaces
 */
export const parseHubSpotPlate = (plate: string): string => {
  if (!plate) return ""

  // Special case for "ohne" (no plate)
  if (plate.toLowerCase() === "ohne") {
    return plate.toUpperCase()
  }

  // Remove any spaces and convert to uppercase
  const cleanPlate = plate.trim().toUpperCase()

  // Check if it's in HubSpot format (CITY-LETTERNUMBER)
  // Example: F-A33 -> F A 33
  const hubspotMatch = cleanPlate.match(/^([A-ZÄÖÜ]{1,3})-([A-ZÄÖÜ]{0,2})(\d{1,4}[EH]?)$/)

  if (hubspotMatch) {
    const [, cityCode, districtLetters, numbers] = hubspotMatch
    // Return in space-separated format for display: "CITY LETTERS NUMBERS"
    // Only include non-empty parts
    const parts = [cityCode]
    if (districtLetters) parts.push(districtLetters)
    if (numbers) parts.push(numbers)
    return parts.join(" ")
  }

  // If not in expected format, return as is
  return plate
}

/**
 * Format a display plate (with spaces) into HubSpot format (CITY-LETTERNUMBER)
 * @param plate - The plate string in display format
 * @returns The plate in HubSpot format with single hyphen
 */
export const formatPlateForHubSpot = (plate: string): string => {
  if (!plate) return ""

  // Special case for "ohne"
  if (plate.toLowerCase() === "ohne") {
    return "OHNE"
  }

  // Remove any existing formatting and convert to uppercase
  const cleanPlate = plate
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, " ")

  // Split into parts
  const parts = cleanPlate.split(" ").filter((part) => part.length > 0)

  if (parts.length === 0) return ""

  // First part is always the city code
  let formatted = parts[0]

  // Combine remaining parts (letters and numbers) after hyphen
  if (parts.length > 1) {
    const restParts = parts.slice(1).join("")
    formatted = `${formatted}-${restParts}`
  }

  return formatted
}

/**
 * Parse any plate format into three parts: city, letters, numbers
 * @param value - The plate string in any format
 * @returns Array of [city, letters, numbers]
 */
export const parsePlateIntoParts = (value: string): [string, string, string] => {
  if (!value) return ["", "", ""]

  // Special handling for OHNE
  if (value.toLowerCase() === "ohne") {
    return ["OHNE", "", ""]
  }

  // Handle HubSpot format: CITY-LETTERNUMBER (e.g., F-A33)
  // First try exact match with separate letters and numbers
  const hubspotMatch = value.trim().match(/^([A-ZÄÖÜ]{1,3})-([A-ZÄÖÜ]{0,2})(\d{1,4}[EH]?)$/i)
  if (hubspotMatch) {
    const [, city, letters, numbers] = hubspotMatch
    return [city || "", letters || "", numbers || ""]
  }

  // Handle format where letters and numbers are combined after hyphen (e.g., F-A32)
  const combinedMatch = value.trim().match(/^([A-ZÄÖÜ]{1,3})-([A-ZÄÖÜ]{0,2}\d{1,4}[EH]?)$/i)
  if (combinedMatch) {
    const [, city, combined] = combinedMatch
    // Split the combined part into letters and numbers
    const letterNumberMatch = combined.match(/^([A-ZÄÖÜ]{0,2})(\d{1,4}[EH]?)$/i)
    if (letterNumberMatch) {
      const [, letters, numbers] = letterNumberMatch
      return [city || "", letters || "", numbers || ""]
    }
  }

  // Handle space-separated format: CITY LETTERS NUMBERS
  const spaceParts = value.trim().split(/\s+/)
  if (spaceParts.length === 3) {
    return [spaceParts[0] || "", spaceParts[1] || "", spaceParts[2] || ""]
  }

  // Handle other formats with multiple hyphens (legacy)
  const parts = value.trim().split(/[\s-]+/)
  // If we have 2 parts and the second part contains both letters and numbers, split it
  if (parts.length === 2) {
    const secondPartMatch = parts[1].match(/^([A-ZÄÖÜ]{0,2})(\d{1,4}[EH]?)$/i)
    if (secondPartMatch) {
      const [, letters, numbers] = secondPartMatch
      return [parts[0] || "", letters || "", numbers || ""]
    }
  }

  return [parts[0] || "", parts[1] || "", parts[2] || ""]
}

/**
 * Validate if a plate string is valid
 * @param plate - The plate string to validate
 * @returns True if valid, false otherwise
 */
export const validatePlate = (plate: string): boolean => {
  if (!plate) return false

  // Special case for "ohne"
  if (plate.toLowerCase() === "ohne") {
    return true
  }

  // Test against the main regex
  return PLATE_REGEX.test(plate.toUpperCase())
}

/**
 * Format plate parts into display string
 * @param city - City code (1-3 letters)
 * @param letters - District letters (0-2 letters)
 * @param numbers - Numbers (1-4 digits, optionally with E or H suffix)
 * @returns Formatted plate string for display
 */
export const formatPlateForDisplay = (city: string, letters: string, numbers: string): string => {
  const parts = [city, letters, numbers].filter((part) => part && part.length > 0)
  return parts.join(" ").toUpperCase()
}

/**
 * Format plate parts into HubSpot string
 * @param city - City code (1-3 letters)
 * @param letters - District letters (0-2 letters)
 * @param numbers - Numbers (1-4 digits, optionally with E or H suffix)
 * @returns Formatted plate string for HubSpot
 */
export const formatPartsForHubSpot = (city: string, letters: string, numbers: string): string => {
  if (!city) return ""

  const restPart = (letters + numbers).toUpperCase()
  if (restPart) {
    return `${city.toUpperCase()}-${restPart}`
  }

  return city.toUpperCase()
}
